import { useState, useEffect, useRef } from 'react'
import { Loader2, Blocks, MoreVertical, Pencil, Copy, Trash, Trash2 } from 'lucide-react'
import type { Workspace } from '@/service/api'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'
import { formatRelativeTime } from '@/utils/timeUtils'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import InlineRenameInput from '@/components/InlineRenameInput'
import { IntuiCard, IntuiCardContent, IntuiCardHeader } from '@/components/intueri/IntuiCard'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'

interface BoardGridV2Props {
  boards: Workspace[]
  onBoardClick: (id: string) => void
  onBoardDelete: (id: string) => void
  onBoardHardDelete: (id: string) => void
}

function BoardCardV2({
  board,
  onClick,
  onDelete,
  onHardDelete,
}: {
  board: Workspace
  onClick: () => void
  onDelete: () => void
  onHardDelete: () => void
}) {
  const { t } = useTranslation()
  const { duplicateBoard, renameBoard } = useAppStore()
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [imageError, setImageError] = useState(false)
  const imageUrlRef = useRef<string | null>(null)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const renameSavingRef = useRef(false)
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus()
      renameInputRef.current.select()
    }
  }, [isRenaming])

  useEffect(() => {
    return () => { if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current) }
  }, [])

  const handleRenameStart = () => {
    setRenameValue(board.name)
    setIsRenaming(true)
    renameSavingRef.current = false
  }

  const handleRenameSave = async () => {
    if (renameSavingRef.current) return
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null }
    if (!renameValue.trim()) {
      setIsRenaming(false)
      return
    }
    renameSavingRef.current = true
    try {
      await renameBoard(board.id, renameValue.trim())
    } finally {
      setIsRenaming(false)
      renameSavingRef.current = false
    }
  }

  const handleRenameBlur = () => {
    blurTimeoutRef.current = setTimeout(handleRenameSave, 150)
  }

  const handleRenameCancel = () => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null }
    setIsRenaming(false)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleRenameSave()
    if (e.key === 'Escape') handleRenameCancel()
  }

  useEffect(() => {
    const loadPreview = async () => {
      setImageError(false)
      if (!board.preview_image) { setImageUrl(null); return }

      try {
        setImageLoading(true)
        const blob = await apiService.getWorkspacePreviewImage(board.id)
        const url = URL.createObjectURL(blob)
        setImageUrl(url)
        imageUrlRef.current = url
      } catch {
        setImageUrl(null)
      } finally {
        setImageLoading(false)
      }
    }
    loadPreview()
    return () => { if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current) }
  }, [board.id, board.preview_image, board.created_at, board.updated_at])

  return (
    <IntuiCard
      className="group cursor-pointer overflow-hidden hover:shadow-md transition-shadow"
      onClick={() => !isRenaming && onClick()}
    >
      {/* Preview */}
      <IntuiCardContent className="h-36 p-0 bg-muted flex items-center justify-center overflow-hidden">
        {imageLoading ? (
          <Loader2 className="size-8 text-muted-foreground animate-spin" />
        ) : imageUrl && !imageError ? (
          <img
            src={imageUrl}
            alt={board.name}
            className="w-full h-full object-cover"
            draggable={false}
            onError={() => setImageError(true)}
          />
        ) : (
          <Blocks className="size-8 text-muted-foreground" />
        )}
      </IntuiCardContent>

      {/* Footer */}
      <IntuiCardHeader className="flex-row items-start justify-between">
        <div className="min-w-0 flex-1">
          {isRenaming ? (
            // Same typography + block layout as the display <p>, so the card
            // keeps its exact footprint while renaming (Enter/blur commits,
            // Escape cancels — no separate confirm button).
            <InlineRenameInput
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleRenameBlur}
              onKeyDown={handleRenameKeyDown}
              onClick={(e) => e.stopPropagation()}
              containerClassName="grid"
              className="text-sm font-medium text-foreground dark:text-gray-100 truncate"
            />
          ) : (
            <p className="text-sm font-medium text-foreground dark:text-gray-100 truncate">
              {board.name}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {t('common:board.opened', 'Opened')}{' '}
            {formatRelativeTime(board.updated_at)}
          </p>
        </div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <IntuiButton variant="ghost" size="icon-sm" aria-label={t('board:actions.more', 'More actions')}>
                <MoreVertical className="size-4 text-muted-foreground" />
              </IntuiButton>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent align="end">
              <IntuiDropdownMenuItem icon={<Pencil />} onClick={handleRenameStart}>
                {t('board:actions.rename', 'Rename')}
              </IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem
                icon={<Copy />}
                onClick={async () => {
                  const result = await duplicateBoard(board.id)
                  if (result) {
                    toast.success(t('board:actions.duplicate_success', 'File duplicated in your workspace'))
                  }
                }}
              >
                {t('board:actions.duplicate', 'Duplicate')}
              </IntuiDropdownMenuItem>
              {/* FS-609: Archive is disabled everywhere until the archive/restore
                  flow is built — today it would hard-delete the board (same as
                  Delete) behind "moved to archive" copy. Wiring (onDelete) is
                  kept intact so re-enabling is just removing `disabled`. */}
              <IntuiDropdownMenuItem variant="destructive" icon={<Trash />} onClick={onDelete} disabled>
                {t('board:actions.archive', 'Archive')}
              </IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem variant="destructive" icon={<Trash2 />} onClick={onHardDelete}>
                {t('board:actions.delete', 'Delete')}
              </IntuiDropdownMenuItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </div>
      </IntuiCardHeader>
    </IntuiCard>
  )
}

export default function BoardGridV2({ boards, onBoardClick, onBoardDelete, onBoardHardDelete }: BoardGridV2Props) {
  // Empty state is owned by BoardListV2 (FS-553), which distinguishes
  // "no boards" from "no search results"; this component only renders cards.
  return (
    <div className="grid grid-cols-4 gap-6">
      {boards.map((board) => (
        <BoardCardV2
          key={board.id}
          board={board}
          onClick={() => onBoardClick(board.id)}
          onDelete={() => onBoardDelete(board.id)}
          onHardDelete={() => onBoardHardDelete(board.id)}
        />
      ))}
    </div>
  )
}
